import { roundQuantity } from '../schedule/unit-of-measure';
import { isValidOutline } from './polygon';
import { slabArea, slabEdge, slabVolume, type Slab, type SlabKind, type SlabOpening } from './slab';

/**
 * O que a conta tira do radier e da laje desenhados (Fase 27, etapa 3 — D7, D8, RN-360, RN-367,
 * RN-369, RN-370).
 *
 * É a irmã de `foundation-totals.ts`: as contas de **cada peça** moram em `slab.ts`, o contrato, e
 * esta é a soma delas na tipologia — uma conta por tipo (`RADIER` e `LAJE`), porque os serviços
 * de uma e de outra nunca se misturam (RN-257). Nenhuma medida de AMBIENTE, PAREDE, PILAR, VIGA,
 * ESTACA, BLOCO ou BALDRAME soma o que está aqui (RN-360).
 *
 * > **A regra em uma linha (RN-370):** só a **laje** perde o que o shaft ocupa, da área e do
 * > volume; o radier fica inteiro, e a região de espessura diferente muda o concreto, e nunca a
 * > área (RN-369).
 */

/** O concreto mora na terceira casa, como o do bloco: duas comeriam o grama do aço dele. */
export const SLAB_VOLUME_DECIMALS = 3;

/** Área e borda: duas casas, como a área do ambiente e o perímetro. */
export const SLAB_AREA_DECIMALS = 2;

export interface SlabTotals {
  /** Quantas peças (un). */
  readonly count: number;
  /** O contorno inteiro — a laje, sem os shafts — de cada peça, somado: a fôrma de fundo (m²). */
  readonly area: number;
  /** O concreto, com a espessura de cada região, somado (m³). */
  readonly volume: number;
  /** Perímetro × espessura da peça, somado: a fôrma de borda (m²). */
  readonly edge: number;
  /** A área mais a borda — a fôrma da laje, que é um serviço só (m²). */
  readonly areaAndEdge: number;
}

/** Sem radier e sem laje desenhados — o ponto de partida, e o que a tipologia sem eles devolve. */
export const NO_SLABS: SlabTotals = { count: 0, area: 0, volume: 0, edge: 0, areaAndEdge: 0 };

/**
 * A peça que conta: a que não foi excluída e tem contorno de verdade — o mesmo critério do
 * bloco (`foundation-totals.ts`).
 */
function contaNaSoma(slab: Pick<Slab, 'kind' | 'outline' | 'deletedAt'>, kind: SlabKind): boolean {
  return slab.kind === kind && slab.deletedAt === undefined && isValidOutline(slab.outline);
}

/**
 * A conta de um tipo de peça numa tipologia.
 *
 * Recebe as peças **do desenho** — o radier é da área comum da Fundação e a laje, da área comum do
 * andar (RN-361) — e escolhe as do tipo pedido. Soma o que cada peça arredondou na casa dela,
 * como o bloco: o total confere com a soma do que a tela mostra de cada uma.
 *
 * `openings` são os shafts das tipologias do **mesmo desenho** (RN-370): só a laje os desconta,
 * e o que cai fora do contorno de uma peça não a toca (`slabArea`, `slabVolume`). O radier os
 * ignora — o buraco da prumada é da laje do andar, e a fundação não passa por ele.
 */
export function slabTotals(
  slabs: readonly Slab[],
  kind: SlabKind,
  openings: readonly SlabOpening[] = [],
): SlabTotals {
  const contam = slabs.filter((slab) => contaNaSoma(slab, kind));
  const buracos = kind === 'LAJE' ? openings : [];
  const soma = (valores: readonly number[], casas: number): number =>
    roundQuantity(
      valores.reduce((total, valor) => total + valor, 0),
      casas,
    );

  const area = soma(
    contam.map((slab) => slabArea(slab, buracos)),
    SLAB_AREA_DECIMALS,
  );
  const edge = soma(contam.map(slabEdge), SLAB_AREA_DECIMALS);
  return {
    count: contam.length,
    area,
    volume: soma(
      contam.map((slab) => slabVolume(slab, buracos)),
      SLAB_VOLUME_DECIMALS,
    ),
    edge,
    areaAndEdge: roundQuantity(area + edge, SLAB_AREA_DECIMALS),
  };
}

/**
 * Os totais multiplicados pelos andares que repetem o desenho — a laje de cada um conta de novo,
 * como o bloco, a estaca e o shaft (RN-167). Cada número volta à casa dele; a contagem é inteira.
 */
export function scaleSlabTotals(totals: SlabTotals, times: number): SlabTotals {
  if (times === 1) return totals;
  const area = (valor: number): number => roundQuantity(valor * times, SLAB_AREA_DECIMALS);
  return {
    count: totals.count * times,
    area: area(totals.area),
    volume: roundQuantity(totals.volume * times, SLAB_VOLUME_DECIMALS),
    edge: area(totals.edge),
    areaAndEdge: area(totals.areaAndEdge),
  };
}
